import axios from "axios";
import { installAuthSync } from "@/lib/authSync";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const ADMIN_API = `${BACKEND_URL}/api`;

const adminApi = axios.create({ baseURL: ADMIN_API });

// v2.7.63+E46.5 · Cross-tab auth sync for admin dispatchers running
// multi-monitor layouts (3-4 screens). Idempotent — safe on hot reload.
installAuthSync({ scope: "admin" });

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("rtg_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── v2.7.65+E187 · Multi-monitor session preservation (session-verify) ──
// A 401 from ANY admin endpoint MUST NOT cascade a logout — dispatchers
// routinely run 3-4 admin windows across monitors, and a single role-
// gated / transient 401 (e.g. a background heartbeat that briefly fails,
// a supervisor-only endpoint being polled from a manager account, a
// microsecond backend hiccup) used to yank every window to the login
// screen. This interceptor now VERIFIES the session with `/admin/me`
// before deciding to redirect:
//
//   • On any 401, we probe `/admin/me` with the current token.
//   • If `/admin/me` returns 200 → the token is still valid; the
//     specific endpoint 401'd for authorization / role reasons or a
//     transient error. Suppress the redirect entirely.
//   • Only when `/admin/me` also 401s do we consider the session
//     genuinely expired and redirect the current window (this window
//     only — no BroadcastChannel cascade, no localStorage mutation).
//
// Guarantees:
//   • Multiple tabs / windows / computers can hold live sessions.
//   • Browser minimize, tab switching, monitor moves, and background
//     polling never force a logout on their own.
//   • Only an explicit "Log Out" click (via AdminContext.logout) OR a
//     truly-expired / revoked token can end a session.
// ─────────────────────────────────────────────────────────────────────
let _redirecting = false;                    // prevents redirect storms
let _verifyingSession = false;               // prevents concurrent /admin/me probes

async function _isSessionStillValid() {
  // Probe /admin/me directly (not via the axios instance to avoid
  // recursing through this interceptor). Any 2xx = session alive.
  const token = localStorage.getItem("rtg_admin_token");
  if (!token) return false;
  if (_verifyingSession) return true;   // another probe already in flight; assume valid
  _verifyingSession = true;
  try {
    const res = await fetch(`${ADMIN_API}/admin/me`, {
      headers: { Authorization: `Bearer ${token}` },
      // Avoid credentials/cookies to keep the probe hermetic.
      credentials: "omit",
      // Never cached — always ask the server.
      cache: "no-store",
    });
    return res.ok;
  } catch (_e) {
    // Network error → don't assume the session is dead. Keep the user
    // on the current page; a real auth failure will surface again.
    return true;
  } finally {
    _verifyingSession = false;
  }
}

adminApi.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err?.response?.status !== 401) return Promise.reject(err);
    if (_redirecting) return Promise.reject(err);
    if (typeof window === "undefined") return Promise.reject(err);
    const path = window.location.pathname || "";
    // Only redirect from an admin-scoped protected page — don't yank a
    // dispatcher off a marketing preview or the public homepage just
    // because a background admin request 401'd.
    const onAdminProtected =
      path.startsWith("/admin") && !path.startsWith("/admin/login");
    if (!onAdminProtected) return Promise.reject(err);

    // v2.7.65+E187 · Session-verify before redirect. If /admin/me still
    // returns 200, the token is valid — the endpoint that just 401'd
    // did so for authorization/role reasons or a transient error, and
    // we must NOT log the operator out of dispatch work.
    const sessionAlive = await _isSessionStillValid();
    if (sessionAlive) {
      // Session is fine. Let the original 401 propagate to the caller
      // so per-feature error handling can render its own message
      // without disrupting the whole console.
      return Promise.reject(err);
    }

    // Session genuinely expired / revoked / token missing → redirect
    // this window only. No cross-window cascade.
    _redirecting = true;
    window.location.href = "/admin/login";
    return Promise.reject(err);
  },
);

export default adminApi;
